"use client";

import {
 createContext,
 use,
 useState,
 useCallback,
 useMemo,
 type ReactNode,
} from "react";

type ValuesTab = "sustainability" | "innovation" | "partnership";

interface AboutContextValue {
 activeValuesTab: ValuesTab;
 setActiveValuesTab: (tab: ValuesTab) => void;
 viewedSections: Set<string>;
 markSectionViewed: (sectionId: string) => void;
 hasSectionBeenViewed: (sectionId: string) => boolean;
}

const AboutContext = createContext<AboutContextValue | null>(null);

interface AboutProviderProps {
 children: ReactNode;
}

export function AboutProvider({ children }: AboutProviderProps) {
 const [activeValuesTab, setActiveValuesTab] =
  useState<ValuesTab>("sustainability");
 const [viewedSections, setViewedSections] = useState<Set<string>>(new Set());

 const markSectionViewed = useCallback((sectionId: string) => {
  setViewedSections((prev) => {
   if (prev.has(sectionId)) return prev;
   const next = new Set(prev);
   next.add(sectionId);
   return next;
  });
 }, []);

 const hasSectionBeenViewed = useCallback(
  (sectionId: string) => viewedSections.has(sectionId),
  [viewedSections],
 );

 const value = useMemo<AboutContextValue>(
  () => ({
   activeValuesTab,
   setActiveValuesTab,
   viewedSections,
   markSectionViewed,
   hasSectionBeenViewed,
  }),
  [activeValuesTab, viewedSections, markSectionViewed, hasSectionBeenViewed],
 );

 return (
  <AboutContext value={value}>{children}</AboutContext>
 );
}

export function useAboutContext(): AboutContextValue {
 const context = use(AboutContext);
 if (!context) {
  throw new Error("useAboutContext must be used within an AboutProvider");
 }
 return context;
}
